import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import { useTranslation } from 'react-i18next';
import { useGetDigitalTrapsPredictionsQuery, useGetRegionalOverviewGroupsQuery } from '@protector/entities/regional-overview/queries';
import { manageAssetGroup } from '@protector/entities/regional-overview/services';
import useSegmentTracking from 'core/utils/segment/useSegmentTracking';
import type { AssetGroup } from './regional-overview.query.models';
import { RegionalOverviewQueryType, RegionalOverviewSource } from './regional-overview.query.models';

export const useGetRegionalOverviewGroups = (organizationId: string) => {
  return useGetRegionalOverviewGroupsQuery(organizationId);
};

export const useGetDigitalTrapsPredictions = (organizationId: string, assetIds: string[]) => {
  return useGetDigitalTrapsPredictionsQuery(organizationId, assetIds);
};

type ManageAssetGroupVariables = {
  group: AssetGroup;
  managementType: AssetGroup['management_type'];
};

export const useManageAssetGroupMutation = () => {
  const queryClient = useQueryClient();
  const [t] = useTranslation();
  const segmentTracking = useSegmentTracking();

  const { notification } = App.useApp();

  return useMutation({
    mutationFn: ({ group }: ManageAssetGroupVariables) => manageAssetGroup(group),
    onSuccess: async (_data, variables) => {
      segmentTracking.track('Digital Traps - Network Management - Saved Additional Info', {
        managementType: variables.managementType
      });
      await queryClient.invalidateQueries([
        RegionalOverviewQueryType.GET_REGIONAL_OVERVIEW_RESULTS_V2,
        RegionalOverviewSource.DIGITAL_TRAPS
      ]);
      notification.success({
        title: t('pages.regional_overview.digital_traps.asset_group_management.save_success')
      });
    },
    onError: (error: Error) => {
      console.error('Error managing asset group:', error);
      notification.error({
        title: t('pages.regional_overview.digital_traps.asset_group_management.error_save')
      });
    }
  });
};
